<template>
  <BaseDialog
    contentClass="advanced"
    ref="dialog"
    sidePanel
    :show-actions="true"
    :persistent="!hasNoChanges"
    :on-close="closeModal"
    @closeModal="closeModal"
  >
    <template slot="title">{{ $t('dialog.filters.advanced.title') }}</template>
    <template slot="content">
      <div class="filter-advanced__content">
        <div>
          <div class="filter-advanced__section">
            <div class="filter-advanced__section--title">{{ $t('dialog.filters.keywords') }}</div>

            <div class="filter-advanced__tree" v-if="keywordCount > 0">
              <KeywordTree v-if="!explore" @onchange="onKeywordTreeChange" ref="keywordTree" />
              <div class="filter-advanced__section--subtitle">{{ $t('search-filters.match-context.name') }}</div>
              <div class="filter-advanced__section--text">{{ $t('search-filters.match-context.subtitle') }}</div>
              <div class="filter-advanced__choices">
                <FormCheckbox
                  v-for="(match, index) in tempMatchContexts"
                  :key="index"
                  class="filter-advanced__checkbox"
                  :label="$t(`search-filters.match-context.values.${match.name}`)"
                  :value="match.value"
                  @input="onMatchContextChange(match, index)"
                  size="small"
                />
              </div>
            </div>
            <div v-else-if="!explore" class="filter-advanced__choices empty">
              <span>{{ $t('page.keywords.no-keywords') }}</span>
            </div>
          </div>
        </div>
        <div class="filter-advanced__section">
          <div class="filter-advanced__section--title">{{ $t('tender-details.informations.contract-type') }}</div>
          <div class="filter-advanced__choices">
            <FormCheckbox
              v-for="(category, index) in tempCategories"
              :key="index"
              class="filter-advanced__checkbox"
              :label="$t(`search-filters.contract-type.values.${category.name}`)"
              :value="category.value"
              @input="onCategoryChange(category, index)"
              size="small"
            />
          </div>
        </div>
        <ZoneTree @onchange="onZonesChange" :searchFilters="searchFilters" ref="zoneTree" />
      </div>
    </template>

    <template slot="actions">
      <div class="filter-advanced__actions">
        <BaseButton @click.native="onClearAll" isTransparent isUnderline isSquare>
          {{ $t('dialog.filters.advanced.clear-all') }}
        </BaseButton>
        <BaseButton @click.native="applyAll" :disabled="hasNoChanges">{{
          $t('dialog.filters.advanced.apply-all')
        }}</BaseButton>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseDialog from '@/components/base/BaseDialog';
import FormCheckbox from '@/components/form/FormCheckbox';
import KeywordTree from '@/components/page-tenders/KeywordTree.vue';
import ZoneTree from '@/components/page-tenders/ZoneTree.vue';

import { CATEGORIES_VALUES, MATCH_CONTEXT_VALUES } from '@/models/search-filters';

import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'FilterAdvancedDialog',
  components: {
    BaseButton,
    BaseDialog,
    FormCheckbox,
    KeywordTree,
    ZoneTree,
  },
  data() {
    return {
      isDirty: false,
      tempMatchContexts: [],
      tempCategories: [],
      tempSearchFilters: {
        keywordIds: [],
        matchContexts: [],
        procurementCategories: [],
        zones: {},
      },
    };
  },
  props: {
    explore: {
      type: Boolean,
      default: false,
    },
    searchFilters: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters('keywords', ['keywordCount']),
    hasNoChanges() {
      return !this.isDirty;
    },
  },
  methods: {
    ...mapActions('keywords', ['getKeywords']),
    buildMatchContexts() {
      return MATCH_CONTEXT_VALUES.map((match) => ({
        name: match,
        value: this.searchFilters.matchContexts.includes(match),
      }));
    },
    buildCategories() {
      return CATEGORIES_VALUES.map((category) => ({
        name: category,
        value: this.searchFilters.procurementCategories.includes(category),
      }));
    },
    applyAll() {
      this.$emit('set-search-filters', this.tempSearchFilters);
      this.closeModal();
    },
    onKeywordTreeChange(ids) {
      this.isDirty = true;
      this.tempSearchFilters.keywordIds = ids;
    },
    onZonesChange(zones) {
      this.isDirty = true;
      this.tempSearchFilters.zones = zones;
    },
    onMatchContextChange(match, index) {
      this.isDirty = true;

      this.tempMatchContexts[index].value = !this.tempMatchContexts[index].value;

      if (this.tempMatchContexts[index].value) {
        this.tempSearchFilters.matchContexts.push(match.name);
      } else {
        const idx = this.tempSearchFilters.matchContexts.indexOf(match.name);
        this.tempSearchFilters.matchContexts.splice(idx, 1);
      }
    },
    onCategoryChange(category, index) {
      this.isDirty = true;

      this.tempCategories[index].value = !this.tempCategories[index].value;

      if (this.tempCategories[index].value) {
        this.tempSearchFilters.procurementCategories.push(category.name);
      } else {
        const idx = this.tempSearchFilters.procurementCategories.indexOf(category.name);
        this.tempSearchFilters.procurementCategories.splice(idx, 1);
      }
    },
    closeModal() {
      this.isDirty = false;
      this.$emit('close-dialog');
    },
    restore() {
      this.tempMatchContexts = this.buildMatchContexts();
      this.tempCategories = this.buildCategories();

      let searchFilters = JSON.parse(JSON.stringify(this.searchFilters));
      this.tempSearchFilters = {
        keywordIds: searchFilters.keywordIds,
        matchContexts: searchFilters.matchContexts,
        procurementCategories: searchFilters.procurementCategories,
        zones: searchFilters.zones,
      };
    },
    onClearAll() {
      this.isDirty = true;
      this.tempMatchContexts.forEach((match) => (match.value = false));
      this.tempCategories.forEach((category) => (category.value = false));
      this.tempSearchFilters = {
        keywordIds: [],
        matchContexts: [],
        procurementCategories: [],
        zones: {},
      };
      this.$refs.zoneTree.clearSelection();
      if (!this.explore) this.$refs.keywordTree.clearSelection();
    },
  },
  async created() {
    await this.getKeywords();
    this.$refs.dialog.isModalOpen = true;
    this.restore();
  },
};
</script>

<style lang="scss">
.base-dialog__advanced {
  width: 850px;
  background: $color-white;

  .base-dialog__header {
    border-bottom: 1px solid $color-border;
  }

  .base-dialog__content {
    padding: 32px;
    font-size: 18px;
    background: $color-white !important;
  }

  .base-dialog__actions {
    padding: 32px;
    background: transparent;
    border-top: 1px solid $color-border;
  }

  @media screen and (max-width: 1080px) {
    width: 100%;
  }

  // Phones: the screen's gutters, and the footer's buttons on one line each.
  @include screen-below('tablet') {
    .base-dialog__content {
      padding: $space-md;
    }

    .base-dialog__actions {
      padding: $space-3 $space-md;
    }

    .filter-advanced__actions {
      gap: $space-3;

      .base-button__element {
        white-space: nowrap;
      }
    }
  }
}

.filter-advanced {
  &__section {
    border-bottom: 1px solid $color-border;
    margin-bottom: 32px;

    &--title {
      font-size: 20px;
      font-weight: bold;
    }

    &--subtitle {
      font-size: 18px;
      padding-top: 32px;
    }

    &--text {
      font-size: 16px;
      color: $color-neutral;
    }
  }

  &__checkbox {
    width: 240px;
    padding: 16px 0;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__clear {
    text-decoration: underline;
    padding: 6px 8px;
    border-radius: $border-radius-root;
    cursor: pointer;
    transition: all 200ms ease;
    font-weight: bold;

    &:hover {
      background: $color-hover;
      transition: all 200ms ease;
    }
  }

  // Phones: one choice per line, a 44px line to tap (its label is the checkbox's).
  @include screen-below('tablet') {
    &__section {
      margin-bottom: $space-md;

      &--subtitle {
        padding-top: $space-md;
      }
    }

    &__checkbox {
      width: 100%;
      padding: 0;

      .form-checkbox__label {
        min-height: 44px;
      }
    }
  }

  &__choices {
    padding: 16px 0;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    &.empty {
      font-size: 14px;
      color: $color-normal-pale;
      font-weight: normal;
      padding: 32px 0;
    }
  }
}
</style>
